import React, { memo, useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/ru";
import PropTypes from "prop-types";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import useController from "./controller";
import CustomPickerDay from "../CustomPickersDay";
import "./style.scss";
import { ReactComponent as CalendarIcon } from "../../../../assets/icons/calendar.svg";
import { ruRU } from "@mui/x-date-pickers/locales";

import birthdayDatePickerTheme, {
  DatePickerStls,
  DialogProps,
  PaperProps,
} from "./styles";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  Button,
  DialogActions,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import CustomCalendarHeader from "../CustomCalendarHeader";

dayjs.extend(customParseFormat);

const minDate = dayjs(new Date(-631177200000)); // 1950y
const eighteenDifference = dayjs(new Date(new Date().getTime() - 567993600000));

// 26.10: дату можно ввести цифрами с клавиатуры. Раньше на телефонах MUI включал
// мобильный DatePicker: поле только для чтения, по тапу выделялся текст или
// открывался календарь, цифры ввести было нельзя. Теперь поле обычное (цифровая
// клавиатура, точки/слэши ставятся сами), календарь открывается иконкой.
const FORMATS = {
  ru: { format: "DD.MM.YYYY", sep: ".", placeholder: "ДД.ММ.ГГГГ" },
  en: { format: "MM/DD/YYYY", sep: "/", placeholder: "MM/DD/YYYY" },
};
const getFormat = () => FORMATS[i18n.language === "ru" ? "ru" : "en"];

// «24102008» → «24.10.2008» по мере ввода
const maskDigits = (digits, sep) =>
  [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join(sep);

const toText = (birthDay) => {
  if (!birthDay) return "";
  const d = dayjs(birthDay);
  return d.isValid() ? d.format(getFormat().format) : "";
};

function BirthDayDatePicker({ birthDay, onBirthdayHandle, isBirthdayValid }) {
  const { t } = useTranslation();
  BirthDayDatePicker.propTypes = {
    birthDay: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onBirthdayHandle: PropTypes.func,
    isBirthdayValid: PropTypes.object,
  };
  const { isDatePickerOpen, openPicker, onAcceptDate, closePicker } =
    useController();
  const [text, setText] = useState(() => toText(birthDay));
  const [isIncomplete, setIsIncomplete] = useState(false);

  // дата пришла снаружи (календарь, данные профиля, смена языка): показываем её,
  // если в поле не набрана уже эта же дата
  useEffect(() => {
    const fromProp = toText(birthDay);
    if (fromProp && fromProp !== text) {
      setText(fromProp);
      setIsIncomplete(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthDay, i18n.language]);

  const handleTextChange = (e) => {
    const { format, sep } = getFormat();
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    const masked = maskDigits(digits, sep);
    setText(masked);
    setIsIncomplete(false);
    if (digits.length === 8) {
      const parsed = dayjs(masked, format, true);
      // невозможная дата (31.02) → пусто, форма не отправится, на blur покажем ошибку
      onBirthdayHandle(parsed.isValid() ? parsed : null);
      if (!parsed.isValid()) setIsIncomplete(true);
    } else if (birthDay) {
      // дату начали менять: старое значение больше не действует
      onBirthdayHandle(null);
    }
  };

  // недописанная или невозможная дата (31.02): показываем ошибку, когда ушли из поля
  const handleBlur = () => {
    const digits = text.replace(/\D/g, "");
    if (!digits.length) return setIsIncomplete(false);
    setIsIncomplete(
      digits.length < 8 || !dayjs(text, getFormat().format, true).isValid()
    );
  };

  const ActionBar = () => {
    return (
      <DialogActions>
        <Button onClick={closePicker}>
          {" "}
          {i18n.language === "ru" ? "Отмена" : "Cancel"}{" "}
        </Button>
        <Button onClick={onAcceptDate}> Ok </Button>
      </DialogActions>
    );
  };

  const getErrorMessage = () => {
    if (isIncomplete) return t("register_page.birthday_valid");
    if (!birthDay?.length) return "";
    if (!isBirthdayValid?.validFormat) {
      return t("register_page.birthday_valid");
    }
    if (!isBirthdayValid?.validAge) {
      return t("register_page.adult_warn");
    }
    return "";
  };

  const pickerValue =
    birthDay && dayjs(birthDay).isValid() ? dayjs(birthDay) : null;

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={i18n.language}
      {...(i18n.language === "ru" && {
        localeText:
          ruRU.components.MuiLocalizationProvider.defaultProps.localeText,
      })}
    >
      <ThemeProvider theme={birthdayDatePickerTheme}>
        <TextField
          value={text}
          onChange={handleTextChange}
          onBlur={handleBlur}
          // всё выделяем: набранные цифры сразу заменяют предзаполненную дату
          onFocus={(e) => {
            const el = e.target;
            setTimeout(() => el.select && el.select(), 0);
          }}
          label={t("birthday")}
          placeholder={getFormat().placeholder}
          error={!!getErrorMessage()}
          helperText={getErrorMessage()}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={openPicker}
                  edge="end"
                  size="small"
                  aria-label={t("birthday")}
                >
                  <CalendarIcon fill="#9B9B9B" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            name: "birthday",
            id: "register-birthday",
            inputMode: "numeric",
            autoComplete: "off",
            maxLength: 10,
          }}
          sx={fieldSx}
        />
        {/* календарь: только окно, его собственное поле скрыто */}
        <MobileDatePicker
          open={isDatePickerOpen}
          onClose={closePicker}
          minDate={minDate}
          maxDate={eighteenDifference}
          onChange={(value) => value && onBirthdayHandle(value)}
          onAccept={onAcceptDate}
          value={pickerValue && pickerValue.locale(i18n.language)}
          referenceDate={eighteenDifference}
          sx={DatePickerStls}
          slots={{
            calendarHeader: CustomCalendarHeader,
            day: CustomPickerDay,
            actionBar: ActionBar,
          }}
          slotProps={{
            toolbar: { hidden: true },
            layout: { sx: { display: "block" } },
            dialog: DialogProps,
            mobilePaper: PaperProps,
            textField: { sx: { display: "none" } },
          }}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
}

const fieldSx = {
  width: "100%",
  maxWidth: "515px",
  marginBottom: "25px",
  "& .MuiOutlinedInput-root": {
    position: "relative",
    fontFamily: "Inter, sans-serif",
    border: "none",
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#FF7253",
      borderWidth: "2px",
    },
    "& fieldset": {
      border: "none",
      borderBottom: "1px solid var(--palette-lines)",
      borderRadius: 0,
      borderColor: "#9B9B9B",
    },
    "&:hover fieldset": {
      borderColor: "#fff",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#fff",
    },
  },
  "& .MuiFormHelperText-root": {
    color: "#FF7253",
    marginLeft: 0,
    marginRight: 0,
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    paddingTop: "4px",
    paddingBottom: "12px",
    paddingLeft: "5px",
    marginTop: "4px",
    fontSize: "1.1111rem",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9B9B9B",
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    fontSize: "16px",
    color: "#9B9B9B",
    fontFamily: "Inter, sans-serif",
    transform: "translate(0px, -9px) scale(0.75)",
    "&.Mui-error": {
      color: "#9B9B9B",
    },
    "&.Mui-focused": {
      color: "#9B9B9B",
    },
  },
};

export default memo(BirthDayDatePicker);
